/**
 * engineNormalJson_Array.ts - Array processing methods for EngineNormalJson
 * Pure ES Module version
 *
 * Uses new HtmlJson format with type prefixes:
 * s=Html, k=JsonKey, K=JsonKeySkip, p=Placeholder, P=PlaceholderSkip,
 * h=Slotholder, H=SlotholderSkip, a=ArrayBlock, A=ArrayBlockSkip, x=SlotHtml, i=Slot, c=Conditional
 */

import { evaluateCondition, getKeysList, parseJsonArray, convertToBoolean } from './engineNormalJson_Helper.js';
import { convertToString } from './engineNormalJson_Merge_Slot.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { JsonArray, JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/index.js';
import {
    TYPE_HTML,
    TYPE_JSONKEY,
    TYPE_JSONKEY_SKIP,
    TYPE_PLACEHOLDER,
    TYPE_PLACEHOLDER_SKIP,
    TYPE_ARRAYBLOCK,
    TYPE_SLOTHTML,
    TYPE_CONDITIONAL,
    META_KEY,
    hasKey,
    getValue,
    isTypePrefixedKey,
    unwrapJsonValue
} from './engineNormalJson_Merge_Constants.js';
import { EngineNormalJson } from './engineNormalJson.js';
import type { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';

import { TemplateOperationKind } from '../../../../Assembler/dist/model/templateOperationKind.js';
interface ResultBuilder {
    value: string;
}

function getKeys(obj: JsonObject | null): string[] {
    if (obj instanceof Map) {
        return Array.from(obj.keys());
    }
    return obj ? Object.keys(obj) : [];
}

export const mergeArrayMethods = {
    processArrayBlock(
        this: EngineNormalJson,
        resultBuilder: ResultBuilder,
        arrayKey: string,
        arrayBlockData: JsonObject,
        appSite: string,
        currentTemplateName: string,
        appView: string | null,
        templateKey: string,
        operations: TemplateOperation[],
        jsonData: JsonObject,
        componentHtml: JsonObject | null,
        genComponentHtml: boolean,
        recursionDepth: number,
        contextJson: JsonObject | null = null,
        flowStack: string[] | null = null,
        configJson: JsonObject | null = null,
        clientMode: boolean = false
    ): void {
        // csharp compares these with OrdinalIgnoreCase and never lowercases (no allocation at all).
        // Node has no allocation-free equivalent, but arrayKey is loop-invariant — fold it once here
        // instead of rebuilding the same lowercased string on every key of every scan below.
        const arrayKeyLower = arrayKey.toLowerCase();
        let actualKey: string | null = null;
        for (const key of getKeys(jsonData)) {
            if (key.toLowerCase() === arrayKeyLower) {
                actualKey = key;
                break;
            }
        }

        if (actualKey === null) {
            for (const key of getKeys(jsonData)) {
                if (key.replace(/s+$/, '').toLowerCase() === arrayKeyLower) {
                    actualKey = key;
                    break;
                }
            }
        }

        let arrayExists = actualKey !== null;
        let arrayHasItems = false;
        let arrayData: JsonArray | null = null;
        let wasValidArrayFormat = false;

        if (arrayExists) {
            const arrayValue = getValue(jsonData, actualKey!);
            if (Array.isArray(arrayValue) || arrayValue instanceof JsonArray) {
                arrayData = this.wrapJsonArrayWithControlElements(arrayValue as JsonArray | unknown[]);
                arrayHasItems = arrayData.length > 0;
                wasValidArrayFormat = true;
            } else if (typeof arrayValue === 'string') {
                const trimmedJson = arrayValue.trim();
                wasValidArrayFormat = trimmedJson.startsWith('[');
                if (wasValidArrayFormat) {
                    arrayData = parseJsonArray(arrayValue);
                    arrayHasItems = arrayData !== null && arrayData.length > 0;
                }
            }
        }

        if (contextJson !== null && !wasValidArrayFormat) {
            for (const key of getKeys(contextJson)) {
                if (key.toLowerCase() === arrayKeyLower ||
                    key.replace(/s+$/, '').toLowerCase() === arrayKeyLower) {
                    const contextValue = getValue(contextJson, key);
                    if (Array.isArray(contextValue) || contextValue instanceof JsonArray) {
                        arrayData = this.wrapJsonArrayWithControlElements(contextValue as JsonArray | unknown[]);
                        actualKey = key;
                        arrayExists = true;
                        arrayHasItems = arrayData.length > 0;
                    }
                    break;
                }
            }
        }

        const blockKeys = getKeysList(Array.from(arrayBlockData.keys()));
        const arrayOperations: TemplateOperation[] = [];

        if (!arrayExists) {
            resultBuilder.value = (resultBuilder.value || '') + this._loader!.defaultReplaceHtml;
        } else {
            let willRenderMainBlock = arrayHasItems;
            let willRenderNegativeBlock = !arrayHasItems;

            // A lone {{^Key}} parses as an array-block negative, but Key may be a BOOLEAN condition
            // (e.g. a context flag like IsAuthenticated) rather than an array. When the value is NOT an
            // array, evaluate it as a boolean: the negative block renders when the condition is FALSE.
            // Resolve with context priority (mirrors Normal's BuildMergedJson). This makes {{^Key}} work
            // for BOTH empty arrays AND false conditions (matching Normal/PreProcess).
            if (arrayData === null) {
                let condVal: unknown = undefined;
                let condFound = false;
                if (contextJson !== null && hasKey(contextJson, arrayKey)) {
                    condVal = getValue(contextJson, arrayKey);
                    condFound = true;
                } else if (actualKey !== null && hasKey(jsonData, actualKey)) {
                    condVal = getValue(jsonData, actualKey);
                    condFound = true;
                }
                if (condFound && !(Array.isArray(condVal) || condVal instanceof JsonArray)) {
                    const condTrue = convertToBoolean(condVal);
                    willRenderMainBlock = condTrue;
                    willRenderNegativeBlock = !condTrue;
                }
            }

            for (const key of blockKeys) {
                if (key === META_KEY) continue;

                const keyType = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';

                if (keyType === TYPE_HTML || keyType === TYPE_SLOTHTML) {
                    const htmlContent = arrayBlockData.get(key).asString() ?? '';
                    resultBuilder.value = (resultBuilder.value || '') + htmlContent;
                } else if (key === 'MainBlock') {
                    if (!willRenderMainBlock) {
                        continue;
                    }

                    const mainBlockContent = arrayBlockData.get('MainBlock').asObject()!;
                    if (arrayData === null) {
                        continue;
                    }

                    let itemIndex = 0;
                    for (const rawItem of arrayData) {
                        let item: unknown = rawItem;
                        if (rawItem instanceof JsonValue) {
                            item = unwrapJsonValue(rawItem);
                        }

                        const itemOperations: TemplateOperation[] = [];
                        const itemProcessedKeys = new Set<string>();
                        const merged = this.mergeArrayItemContent(
                            mainBlockContent,
                            item as JsonObject,
                            appSite,
                            currentTemplateName,
                            appView,
                            templateKey + '_ArrayItem',
                            itemOperations,
                            jsonData,
                            componentHtml,
                            genComponentHtml,
                            itemProcessedKeys,
                            recursionDepth + 1,
                            contextJson,
                            flowStack,
                            configJson,
                            clientMode
                        );
                        resultBuilder.value = (resultBuilder.value || '') + merged;

                        if (itemIndex < this._loader!.maxArrayOperationItems) {
                            arrayOperations.push(...itemOperations);
                        }
                        itemIndex++;
                    }
                } else if (key === 'NegativeBlock') {
                    if (!willRenderNegativeBlock) {
                        continue;
                    }

                    const negativeBlockContent = arrayBlockData.get('NegativeBlock').asObject()!;
                    const merged = this.mergeTemplate(
                        negativeBlockContent,
                        appSite,
                        currentTemplateName,
                        appView,
                        templateKey,
                        [],
                        jsonData,
                        componentHtml,
                        flowStack,
                        genComponentHtml,
                        contextJson,
                        configJson,
                        recursionDepth + 1,
                        null,
                        null,
                        null,
                        null,
                        clientMode
                    );
                    resultBuilder.value = (resultBuilder.value || '') + merged;
                }
            }
        }

        let replacedWith: string;
        let kind: TemplateOperationKind;
        const hasNegativeBlock = arrayBlockData.has('NegativeBlock');

        if (!arrayExists) {
            kind = TemplateOperationKind.ArrayIteration;
            replacedWith = `${this._loader!.defaultReplaceHtml} (array not found)`;
        } else if (!arrayHasItems) {
            if (hasNegativeBlock) {
                kind = TemplateOperationKind.NegativeCondition;
                replacedWith = '(shown)';
            } else {
                kind = TemplateOperationKind.ArrayIteration;
                replacedWith = `${currentTemplateName}_${actualKey} (0 items, 0 logged)`;
            }
        } else {
            kind = TemplateOperationKind.ArrayIteration;
            const loggedCount = arrayData!.length < this._loader!.maxArrayOperationItems ? arrayData!.length : this._loader!.maxArrayOperationItems;
            replacedWith = `${currentTemplateName}_${actualKey} (${arrayData!.length} items, ${loggedCount} logged)`;
        }

        // Target casing differs by op type per Normal HTML engine's behavior:
        //   - NegativeCondition ({{^Key}}): uses data-source key casing.
        //   - ArrayIteration ({{@Key}}): uses Normal's matched-variant form (tries
        //     [as-is, lower, lower without trailing 's', lower+'s'] against the template literal).
        const useNegativeTarget = arrayExists && hasNegativeBlock && !arrayHasItems;
        let targetKey: string;
        if (useNegativeTarget) {
            targetKey = actualKey ?? arrayKey;
        } else {
            targetKey = arrayKey;
            if (actualKey !== null) {
                const keyNorm = actualKey.toLowerCase();
                const variants = [actualKey, keyNorm, keyNorm.replace(/s+$/, ''), keyNorm + 's'];
                for (const variant of variants) {
                    if (variant.toLowerCase() === arrayKey.toLowerCase()) {
                        targetKey = variant;
                        break;
                    }
                }
            }
        }
        operations.push({
            templateName: templateKey,
            kind: kind,
            target: useNegativeTarget ? '{{^' + targetKey + '}}' : '{{@' + targetKey + '}}',
            replacedWith,
            operations: arrayOperations
        } as TemplateOperation);
    },

    mergeArrayItemContent(
        this: EngineNormalJson,
        blockContent: JsonObject,
        itemData: JsonObject,
        appSite: string,
        currentTemplateName: string,
        appView: string | null,
        templateKey: string,
        operations: TemplateOperation[],
        jsonData: JsonObject,
        componentHtml: JsonObject | null,
        genComponentHtml: boolean,
        processedKeys: Set<string>,
        recursionDepth: number,
        contextJson: JsonObject | null = null,
        flowStack: string[] | null = null,
        configJson: JsonObject | null = null,
        clientMode: boolean = false
    ): string {
        processedKeys = processedKeys || new Set<string>();
        let result = '';
        const keys = getKeysList(Array.from(blockContent.keys()));

        // Stage KeyReplace/KeyMissing/Component for canonical emission order. Normal HTML engine
        // emits KeyReplace in merged data-source key iteration order (itemData then jsonData),
        // KeyMissing in template scan order, ComponentReplace last.
        const matchedItemKeys = new Set<string>();   // lowercase keys matched in itemData
        const matchedJsonKeys = new Set<string>();    // lowercase keys matched only in jsonData
        const missingKeysInScanOrder: string[] = [];  // original-case missing keys, scan order
        const componentPhaseOps: TemplateOperation[] = [];

        for (const key of keys) {
            if (key === META_KEY) continue;

            const value = blockContent.get(key);
            const keyType = isTypePrefixedKey(key) && key.length > 0 ? key[0] : '\0';
            const keyNameFromKey = GenerateHtmlJson.getKeyName(key);
            const valueStr = value.asString() ?? '';

            if (keyType === TYPE_HTML || keyType === TYPE_SLOTHTML) {
                result += valueStr;
            } else if (keyType === TYPE_JSONKEY || keyType === TYPE_JSONKEY_SKIP) {
                const keyName = keyNameFromKey;
                let foundInItem = false;
                let foundInJson = false;
                let replacementValue = '';

                if (hasKey(itemData, keyName)) {
                    replacementValue = convertToString(getValue(itemData, keyName));
                    foundInItem = true;
                } else if (hasKey(jsonData, keyName)) {
                    replacementValue = convertToString(getValue(jsonData, keyName));
                    foundInJson = true;
                }

                if (foundInItem || foundInJson) {
                    result += replacementValue;
                    if (foundInItem) matchedItemKeys.add(keyName.toLowerCase());
                    else matchedJsonKeys.add(keyName.toLowerCase());
                } else {
                    result += this._loader!.defaultReplaceHtml;
                    missingKeysInScanOrder.push(keyName);
                }
            } else if (keyType === TYPE_PLACEHOLDER || keyType === TYPE_PLACEHOLDER_SKIP) {
                const placeholderStr = value.asString() ?? '';
                const componentName = keyNameFromKey || (placeholderStr.length > 4 ? placeholderStr.substring(2, placeholderStr.length - 2) : '');
                const newFlowStack = [...(flowStack || []), componentName];

                const startGetArrayComp = performance.now();
                const { htmlJson: componentHtmlJson, dataJson: componentDataJson, resolvedTemplateName, resolvedAppSite: resolvedComponentAppSite } =
                    this._loader!.getTemplateWithView(appSite, currentTemplateName, componentName, appView, newFlowStack, configJson, clientMode);
                this._getTemplateNanos += EngineNormalJson.msToNanos(performance.now() - startGetArrayComp);
                this._getTemplateCount++;

                const arrViewHtmlJson = componentHtmlJson;

                if (arrViewHtmlJson) {
                    const childOperations: TemplateOperation[] = [];
                    const mergeJsonData = componentDataJson || itemData;
                    const componentKey = `${resolvedComponentAppSite}_${resolvedTemplateName || componentName}`;

                    if (genComponentHtml && componentHtml) {
                        componentHtml.set(resolvedTemplateName || componentName, '');
                    }

                    const merged = this.mergeTemplate(
                        arrViewHtmlJson,
                        appSite,
                        componentName,
                        appView,
                        componentKey,
                        childOperations,
                        mergeJsonData instanceof JsonObject ? mergeJsonData : new JsonObject(),
                        componentHtml,
                        newFlowStack,
                        genComponentHtml,
                        contextJson,
                        configJson,
                        recursionDepth + 1,
                        null,
                        null,
                        null,
                        null,
                        clientMode
                    );
                    result += merged;

                    if (genComponentHtml && componentHtml) {
                        componentHtml.set(resolvedTemplateName || componentName, merged);
                    }

                    // Route ComponentReplace into componentPhaseOps so it emits after
                    // KeyReplace/KeyMissing — matches Normal's phase ordering.
                    componentPhaseOps.push({
                        templateName: templateKey,
                        kind: TemplateOperationKind.ComponentReplace,
                        target: valueStr,
                        replacedWith: componentKey,
                        operations: childOperations
                    } as TemplateOperation);
                } else {
                    result += valueStr;
                }
            } else if (keyType === TYPE_CONDITIONAL) {
                const conditionalBlockData = value.asObject();
                if (!conditionalBlockData) continue;
                result += this.mergeArrayItemConditionalBlock(
                    conditionalBlockData,
                    keyNameFromKey,
                    itemData,
                    appSite,
                    currentTemplateName,
                    appView,
                    templateKey,
                    operations,
                    jsonData,
                    componentHtml,
                    genComponentHtml,
                    processedKeys,
                    recursionDepth,
                    contextJson,
                    flowStack,
                    configJson,
                    clientMode
                );
            } else if (keyType === TYPE_ARRAYBLOCK) {
                const nestedArrayKey = keyNameFromKey;
                const nestedArrayBlockData = value.asObject();
                if (!nestedArrayBlockData) continue;
                // Attribute the nested array op to the component (e.g. UserList), not the synthetic
                // "_ArrayItem" item context — matches Normal's op TemplateName.
                const componentKey = templateKey.endsWith('_ArrayItem')
                    ? templateKey.substring(0, templateKey.length - '_ArrayItem'.length)
                    : templateKey;
                const nestedResultBuilder: ResultBuilder = { value: '' };
                this.processArrayBlock(
                    nestedResultBuilder,
                    nestedArrayKey,
                    nestedArrayBlockData,
                    appSite,
                    currentTemplateName,
                    appView,
                    componentKey,
                    operations,
                    itemData,
                    componentHtml,
                    genComponentHtml,
                    recursionDepth,
                    contextJson,
                    flowStack,
                    configJson,
                    clientMode
                );
                result += nestedResultBuilder.value;
            }
        }

        // Emit KeyReplace ops in itemData order first (Normal's merged-data iteration),
        // then jsonData-only keys not already covered by itemData. KeyMissing in scan order.
        // ComponentReplace ops last (matches Normal's traversal phase).
        const emittedLower = new Set<string>();
        for (const itemKey of getKeys(itemData)) {
            const itemKeyLower = itemKey.toLowerCase();
            if (matchedItemKeys.has(itemKeyLower) && !processedKeys.has(itemKeyLower)) {
                processedKeys.add(itemKeyLower);
                operations.push({
                    templateName: templateKey,
                    kind: TemplateOperationKind.KeyReplace,
                    target: `{{$${itemKey}}}`,
                    replacedWith: 'ArrayItem_' + itemKey,
                    operations: [],
                    paramJson: ''
                } as TemplateOperation);
                emittedLower.add(itemKeyLower);
            }
        }
        for (const jsonKey of getKeys(jsonData)) {
            const jsonKeyLower = jsonKey.toLowerCase();
            if (emittedLower.has(jsonKeyLower)) continue;
            if (matchedJsonKeys.has(jsonKeyLower) && !processedKeys.has(jsonKeyLower)) {
                processedKeys.add(jsonKeyLower);
                operations.push({
                    templateName: templateKey,
                    kind: TemplateOperationKind.KeyReplace,
                    target: `{{$${jsonKey}}}`,
                    replacedWith: currentTemplateName + '_' + jsonKey,
                    operations: [],
                    paramJson: ''
                } as TemplateOperation);
            }
        }
        for (const missingKey of missingKeysInScanOrder) {
            const missingKeyLower = missingKey.toLowerCase();
            if (!processedKeys.has(missingKeyLower)) {
                processedKeys.add(missingKeyLower);
                operations.push({
                    templateName: templateKey,
                    kind: TemplateOperationKind.KeyMissing,
                    target: `{{$${missingKey}}}`,
                    replacedWith: currentTemplateName + '_' + missingKey,
                    operations: [],
                    paramJson: ''
                } as TemplateOperation);
            }
        }
        operations.push(...componentPhaseOps);

        return result;
    },

    mergeArrayItemConditionalBlock(
        this: EngineNormalJson,
        conditionalBlockData: JsonObject,
        fallbackConditionalKey: string,
        itemData: JsonObject,
        appSite: string,
        currentTemplateName: string,
        appView: string | null,
        templateKey: string,
        operations: TemplateOperation[],
        jsonData: JsonObject,
        componentHtml: JsonObject | null,
        genComponentHtml: boolean,
        processedKeys: Set<string>,
        recursionDepth: number,
        contextJson: JsonObject | null,
        flowStack: string[] | null,
        configJson: JsonObject | null = null,
        clientMode: boolean = false
    ): string {
        const conditionalKey = (conditionalBlockData.get('ConditionalKey').asString() ?? '') || fallbackConditionalKey;
        let conditionMet = evaluateCondition(conditionalKey, itemData);
        const isNegativeRaw = conditionalBlockData.get('IsNegative');
        const isNegative = isNegativeRaw instanceof JsonValue
            ? (!isNegativeRaw.isNull && (isNegativeRaw.asBool() === true || isNegativeRaw.asString() === 'true'))
            : (isNegativeRaw === true || isNegativeRaw === 'true');
        if (isNegative) {
            conditionMet = !conditionMet;
        }

        const { positiveContent, negativeContent } = this.findPairedConditionalBlocks(conditionalBlockData);
        const selectedContent = this.selectConditionalContentForCondition(conditionMet, positiveContent, negativeContent);

        if (!selectedContent) {
            return '';
        }

        return this.mergeArrayItemContent(
            selectedContent,
            itemData,
            appSite,
            currentTemplateName,
            appView,
            templateKey,
            operations,
            jsonData,
            componentHtml,
            genComponentHtml,
            processedKeys,
            recursionDepth,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );
    }
};
